import { Tooltip as ReactTooltip } from 'react-tooltip'

import { ArrowRightIcon } from '@heroicons/react/24/outline'

import ChainToken from '@src/components/ChainToken'
import { OrderItemTableRow } from '@src/components/OrderHistoryTable'
import { getBlockExplorerUrlFromChainId } from '@src/utils/getBlockExplorerUrl'
import { useConfigStore } from '@src/zustand_stores'

type OrderHistoryItemProps = {
    item: OrderItemTableRow
    isPending?: boolean
    tooltipContent?: string
}

const OrderHistoryItemRow = ({ item, isPending, tooltipContent }: OrderHistoryItemProps) => {
    const availableNetworks = useConfigStore(config => config.availableNetworks)

    const shortenAddress = (address: string) => {
        return address.length > 10
            ? address.substring(0, 7) + '...' + address.substring(address.length - 7, address.length)
            : address
    }

    const formatAmount = (amount: string | number, decimals: number) => {
        const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount
        return numericAmount / 10 ** decimals
    }

    const formatDuration = (seconds: number) => {
        if (seconds < 60) return `${Math.round(seconds)}s`
        const minutes = Math.floor(seconds / 60)
        const remainingSeconds = Math.round(seconds % 60)
        return `${minutes}m ${remainingSeconds}s`
    }

    const createdAt = new Date(item.created_at)
    const filledAt = item.filled_at ? new Date(item.filled_at) : null
    const duration = filledAt ? (filledAt.getTime() - createdAt.getTime()) / 1000 : null // duration in seconds

    // Check if the order is expired
    const expirationTime = item.expires_at ? new Date(item.expires_at).getTime() : 0
    const currentTime = new Date().getTime()
    const isExpired = expirationTime && currentTime > expirationTime

    return (
        <>
            <td className='px-4 py-4'>
                <div className='flex items-center space-x-2'>
                    <ChainToken
                        networkIcon={availableNetworks[item.originChainId]?.icon}
                        tokenLogo={item.sellToken.logoURI}
                        className='h-8 w-8'
                    />
                    <div className='flex flex-col'>
                        <span className='font-medium'>{item.sellToken.symbol}</span>
                        <span className='text-sm text-gray-400'>
                            {formatAmount(item.sellQuantity, item.sellToken.decimals).toLocaleString('en-us', {
                                maximumFractionDigits: 8,
                            })}
                        </span>
                    </div>
                </div>
            </td>
            <td className='w-8 py-4'>
                <ArrowRightIcon className='h-4 w-4 text-gray-400' />
            </td>
            <td className='px-4 py-4'>
                <div className='flex items-center space-x-2'>
                    <ChainToken
                        networkIcon={availableNetworks[item.destChainId]?.icon}
                        tokenLogo={item.buyToken.logoURI}
                        className='h-8 w-8'
                    />
                    <div className='flex flex-col'>
                        <span className='font-medium'>{item.buyToken.symbol}</span>
                        <span className='text-sm text-gray-400'>
                            {formatAmount(item.buyQuantity, item.buyToken.decimals).toLocaleString('en-us', {
                                maximumFractionDigits: 8,
                            })}
                        </span>
                    </div>
                </div>
            </td>
            <td className='px-4 py-4'>
                {item.originChainTxHash && (
                    <div className='flex items-center space-x-2'>
                        <img
                            src={availableNetworks[item.originChainId]?.icon}
                            className='h-5 w-5 rounded-full opacity-75'
                        />
                        <a
                            target='_blank'
                            href={`${getBlockExplorerUrlFromChainId(item.originChainId)}/${item.originChainTxHash}`}
                            className='text-sm text-gray-400 transition-colors hover:text-gray-200'
                            rel='noreferrer'
                        >
                            <span>{shortenAddress(item.originChainTxHash)}</span>
                        </a>
                    </div>
                )}
            </td>
            <td className='px-4 py-4'>
                {item.destChainTxHash ? (
                    <div className='flex items-center space-x-2'>
                        <img
                            src={availableNetworks[item.destChainId]?.icon}
                            className='h-5 w-5 rounded-full opacity-75'
                        />
                        <a
                            target='_blank'
                            href={`${getBlockExplorerUrlFromChainId(item.destChainId)}/${item.destChainTxHash}`}
                            className='text-sm text-gray-400 transition-colors hover:text-gray-200'
                            rel='noreferrer'
                        >
                            <span>{shortenAddress(item.destChainTxHash)}</span>
                        </a>
                    </div>
                ) : isPending ? (
                    <div className='flex items-center space-x-2'>
                        <img
                            src={availableNetworks[item.destChainId]?.icon}
                            className='h-5 w-5 rounded-full opacity-75'
                        />
                        {isExpired ? (
                            <span className='text-sm text-red-400'>Expired</span>
                        ) : (
                            <>
                                <span
                                    className='cursor-help text-sm text-yellow-400'
                                    data-tooltip-id={`cctp-tooltip-${item.originChainTxHash}`}
                                >
                                    Pending Confirmation
                                </span>
                                <ReactTooltip
                                    id={`cctp-tooltip-${item.originChainTxHash}`}
                                    content={tooltipContent}
                                    place='top'
                                    className='!z-50 max-w-[300px] !bg-[#141618] !opacity-100'
                                    clickable={true}
                                    closeOnEsc={true}
                                    closeOnClickOutside={true}
                                />
                            </>
                        )}
                    </div>
                ) : (
                    <div className='flex items-center space-x-2'>
                        <img
                            src={availableNetworks[item.destChainId]?.icon}
                            className='h-5 w-5 rounded-full opacity-75'
                        />
                        <span className='text-sm text-gray-400'>-</span>
                    </div>
                )}
            </td>
            <td className='px-4 py-4'>
                <div className='flex flex-col space-y-1'>
                    <span className='text-sm text-gray-400'>{new Date(item.created_at).toLocaleString()}</span>
                    {duration && (
                        <div className='flex items-center space-x-1.5'>
                            <div className='h-[6px] max-h-[6px] min-h-[6px] w-[6px] min-w-[6px] max-w-[6px] rounded-full bg-green-500/50'></div>
                            <span className='text-xs text-green-400'>Completed in {formatDuration(duration)}</span>
                        </div>
                    )}
                </div>
            </td>
        </>
    )
}

export default OrderHistoryItemRow
